import { motion, useAnimation, useInView, Variants } from "framer-motion";
import { useEffect, useRef, useState } from "react";

// Service-specific images
const serviceImages = {
  hemstädning: "https://images.unsplash.com/photo-1600121848594-d8644e57abab?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=60",
  kontorsstädning: "https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=60",
  flyttstädning: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=60",
  storstädning: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=60",
  fönsterputs: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=60",
  trappstädning: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=60"
};

const serviceLinks: Record<string, string> = {
  hemstädning: "/hemstadning",
  kontorsstädning: "#office",
  flyttstädning: "#moving",
  storstädning: "#deep",
  fönsterputs: "#fonsterputs",
  trappstädning: "#trappstadning",
};

const ServiceBox = ({ service, index }: { service: any, index: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  // Intersection observer for entry animation without framer-motion
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: "0px 0px -100px 0px",
        threshold: 0.2,
      }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group relative aspect-square overflow-hidden bg-gray-50 border-[3px] border-red-500 sm:border-[4px] w-full h-full transition-all duration-200 ease-out hover:-translate-y-1 hover:scale-[1.01] hover:shadow-lg ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}
      style={{
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        transform: 'translate3d(0, 0, 0)',
        willChange: 'transform, opacity',
        transitionProperty: 'transform, opacity, box-shadow, border-color',
        transitionDuration: '360ms',
        transitionTimingFunction: 'cubic-bezier(0.33, 0, 0.2, 1)',
        transitionDelay: visible ? `${index * 60}ms` : '0ms',
      }}
    >
      {/* Background Image */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src={serviceImages[service.id as keyof typeof serviceImages]}
          alt={`Bild på ${service.title.toLowerCase()}`}
          width={500}
          height={500}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-opacity duration-300 opacity-100"
          style={{
            transform: 'translateZ(0)',
            willChange: 'transform, opacity',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
          }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-transparent"></div>
      </div>
      
      {/* Content */}
      <div className="relative h-full flex flex-col justify-end p-4 sm:p-6 text-white text-left">
        <div className="w-full">
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold pb-1.5 sm:pb-3 relative inline-block leading-tight">
            <a
              href={serviceLinks[service.id] ?? '#'}
              className="inline-block max-w-full break-words hover:text-red-200 transition-colors duration-150"
              aria-label={`Läs mer om ${service.title}`}
            >
              {service.title}
            </a>
            <span className="absolute bottom-0 left-0 w-full h-1 bg-red-500 transform translate-y-1"></span>
          </h3>
          <div className="mt-3 sm:mt-2">
            <a 
              href={serviceLinks[service.id] ?? '#'}
              className="inline-flex items-center justify-center bg-red-500 text-white font-semibold py-2 px-4 sm:py-2 sm:px-6 text-sm sm:text-base rounded-full border-2 border-red-500 hover:bg-red-600 hover:border-red-600 transition-all duration-200 w-full sm:w-auto text-center hover:scale-105"
              aria-label={`Läs mer om ${service.title}`}
            >
              Läs mer
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const Services = () => {
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { 
    once: true,
    margin: "0px 0px -10% 0px",
    amount: 0.1
  });

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [controls, isInView]);

  const pathVariants: Variants = {
    hidden: { 
      pathLength: 0, 
      opacity: 0,
      scale: 0.98
    },
    visible: (i: number) => ({
      pathLength: 1,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 1.2,
        ease: [0.16, 1, 0.3, 1],
        delay: 0.2 + (i * 0.05),
        pathLength: {
          duration: 1.4,
          ease: [0.22, 1, 0.36, 1]
        },
        opacity: {
          duration: 0.8,
          ease: [0.4, 0, 0.2, 1]
        },
        scale: {
          duration: 1,
          ease: [0.22, 1, 0.36, 1]
        }
      }
    })
  };
  const services = [
    {
      id: "hemstädning",
      title: "Hemstädning",
      description: "Regelbunden städning av ditt hem med flexibla avtal. Våra erfarna städare använder miljövänliga produkter.",
    },
    {
      id: "kontorsstädning",
      title: "Kontorsstädning",
      description: "Professionell städning av kontor och företagslokaler. Anpassade lösningar för alla branscher.",
    },
    {
      id: "flyttstädning",
      title: "Flyttstädning",
      description: "Garanterad flyttstädning med RUT-avdrag. Vi ser till att din bostad är fläckfri inför flytten.",
    },
    {
      id: "storstädning",
      title: "Storstädning",
      description: "Grundlig storstädning av hela hemmet eller kontoret. Perfekt för vår- och höststädning.",
    },
    {
      id: "fönsterputs",
      title: "Fönsterputs",
      description: "Kristallklara fönster året runt. Vi putsar både in- och utsida med professionell utrustning.",
    },
    {
      id: "trappstädning",
      title: "Trappstädning",
      description: "Regelbunden städning av trapphus och gemensamma utrymmen i bostadsrättsföreningar.",
    },
  ];

  return (
    <section ref={ref} id="tjanster" className="py-12 sm:py-16 md:py-24 bg-white">
      <div className="flex justify-center">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative text-center mb-16">
          <div className="hidden lg:block absolute left-0 top-[36%] transform -translate-y-1/2 w-1/4 xl:w-1/3">
            <motion.svg 
              viewBox="0 0 1200 80" 
              className="w-full h-4 sm:h-5 text-red-500"
              initial="hidden"
              animate={controls}
            >
              <motion.path 
                className="hidden sm:block"
                d="M0,40 C200,80 400,0 600,40 C800,80 1000,0 1200,40" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="12" 
                strokeLinecap="round"
                variants={pathVariants}
              />
              <motion.path 
                className="sm:hidden"
                d="M0,40 C150,60 300,20 400,40 C500,60 650,20 800,40 C950,60 1100,20 1200,40" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="10" 
                strokeLinecap="round"
                variants={pathVariants}
              />
            </motion.svg>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-red-600 mb-3 sm:mb-4 font-['Raleway'] uppercase tracking-wider relative z-10 px-6 sm:px-12 md:px-32 max-w-5xl mx-auto">VÅRA TJÄNSTER</h2>
          <div className="hidden lg:block absolute right-0 top-[36%] transform -translate-y-1/2 w-1/4 xl:w-1/3">
            <motion.svg 
              viewBox="0 0 1200 80" 
              className="w-full h-4 sm:h-5 text-red-500"
              initial="hidden"
              animate={controls}
            >
              <motion.path 
                className="hidden sm:block"
                d="M1200,40 C1000,80 800,0 600,40 C400,80 200,0 0,40" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="12" 
                strokeLinecap="round"
                variants={pathVariants}
              />
              <motion.path 
                className="sm:hidden"
                d="M1200,40 C1050,60 900,20 800,40 C700,60 550,20 400,40 C250,60 100,20 0,40" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="10" 
                strokeLinecap="round"
                variants={pathVariants}
              />
            </motion.svg>
          </div>
          <p className="mt-3 sm:mt-4 text-muted-foreground max-w-2xl mx-auto px-4 sm:px-0">
            Professionella städtjänster för hem och kontor med fokus på kvalitet och miljö
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 px-4 sm:px-6 md:px-8 max-w-7xl mx-auto">
          {services.map((service, index) => (
            <ServiceBox key={service.id} service={service} index={index} />
          ))}
        </div>
      </div>
      </div>
    </section>
  );
};

export default Services;
